import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { CMSSection } from '@/components/Cms/CMSSection';
import { Loader } from '@/components/Loader';
import { Price } from '@/components/Price';
import { ProductDetail } from '@/components/ProductDetail';
import { Typography } from '@/components/Typography';
import {
  APOLLO_CLIENTS,
  CustomContext,
  initializeApollo,
} from '@/graphql/client';
import type {
  OrderAppProductVariantBusinessDetailsQuery,
  OrderAppProductVariantBusinessDetailsQueryVariables,
} from '@/graphql/orderApp/api';
import { ProductVariantBusinessDetailsDocument } from '@/graphql/orderApp/api';
import type {
  ProductsBySkusQuery,
  ProductVariantWatchBasicDetailsQuery,
} from '@/graphql/saleor/api';
import { useIsMdUp } from '@/hooks/useMedia';
import { useRouter } from '@/hooks/useRouter';
import { filterEnabledStraps } from '@/integrations/algolia/helpers';
import type { StrapHit, WatchHit } from '@/integrations/algolia/types';
import type { PlaceholderType } from '@/integrations/contentful/types';
import {
  getDropdownValue,
  getMultiselectValue,
  getNumericValue,
} from '@/lib/attributes';
import {
  getCollectionTranslatedName,
  getProductTranslatedName,
} from '@/lib/i18n';
import { getProductType } from '@/lib/product';
import { fetchProductDetail, fetchProductsBySkus } from '@/lib/product/api';
import { getGqlRegionFromLocale, getRegionFromLocale } from '@/lib/regions';
import type { CtaType } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { pdpMessages } from '@/messages/product';
import { TechnicalData } from '@/views/ProductDetailView/components/TechnicalData/TechnicalData';
import { highlightCollection } from '@/views/WatchView/components';
import ChangeStrapButton from '@/views/WatchView/components/ChangeStrapButton/ChangeStrapButton';
import ChangeStrapModal from '@/views/WatchView/components/ChangeStrapModal/ChangeStrapModal';

import { AppointmentForm } from '../AppointmentForm/AppointmentForm';
import { scrollToTop } from '../RetailerContactUs/helpers';
import { RetailerContactUs } from '../RetailerContactUs/RetailerContactUs';

import * as S from './RetailerProductDetail.styles';

interface RetailerProductDetailProps {
  multiDoors?: string[] | null;
  product: WatchHit | StrapHit;
  retailerId?: string;
}

interface FetchedProductData {
  _forceUpdate?: number;
  businessData?: OrderAppProductVariantBusinessDetailsQuery;
  error?: Error;
  loading: boolean;
  variantDetails?: ProductVariantWatchBasicDetailsQuery;
}

interface TechnicalDataAttribute {
  name: string;
  value: string | null | undefined;
}

interface TechnicalDataSectionType {
  attributes: TechnicalDataAttribute[];
  title: string;
}

interface BundleVariationOption {
  alt: string | null | undefined;
  coords: { x1: number; x2: number; y1: number; y2: number };
  image: string | undefined;
  slug: string | null | undefined;
  strapColor: string | null | undefined;
  strapColorCode: string | null | undefined;
  strapMaterial: string | null | undefined;
}

export const RetailerProductDetail: FC<RetailerProductDetailProps> = ({
  multiDoors,
  product,
  retailerId: propRetailerId,
}) => {
  const isDesktopView = useIsMdUp();
  const { locale } = useRouter();

  const { formatMessage } = useIntl();
  const [productData, setProductData] = useState<FetchedProductData>({
    loading: true,
  });

  // Use the retailerId from props or try to get it from the URL
  const [retailerId, setRetailerId] = useState<string | undefined>(
    propRetailerId,
  );

  const [offersAppointment, setOffersAppointment] = useState<boolean | null>(
    null,
  );
  const [isLoadingRetailer, setIsLoadingRetailer] = useState(false);

  const [isChangeStrapModalOpen, setIsChangeStrapModalOpen] = useState(false);
  const [bundleVariationOptions, setBundleVariationOptions] = useState<
    BundleVariationOption[]
  >([]);
  const [enabledStrapSkus, setEnabledStrapSkus] = useState<string[]>([]);
  const [bundleVariations, setBundleVariations] = useState<string[]>([]);
  const [strapData, setStrapData] = useState<ProductsBySkusQuery | null>(null);
  const [hasDifferentStraps, setHasDifferentStraps] = useState(false);

  // Ref for scrolling to form
  const formRef = useRef<HTMLDivElement>(null);

  const sku =
    productData?.variantDetails?.productVariant?.sku ?? product.objectID;

  useEffect(() => {
    if (!sku) {
      setProductData({
        loading: false,
        error: new Error('No SKU provided'),
      });
      return;
    }

    const fetchProductData = async () => {
      try {
        const regionVariables = getGqlRegionFromLocale(locale);
        const client = initializeApollo();

        const [variantDetails, { data: businessData }] = await Promise.all([
          fetchProductDetail({
            sku: sku.toUpperCase(),
            channel: regionVariables.channel,
            languageCode: regionVariables.languageCode,
          }),

          client.query<
            OrderAppProductVariantBusinessDetailsQuery,
            OrderAppProductVariantBusinessDetailsQueryVariables
          >({
            query: ProductVariantBusinessDetailsDocument,
            variables: {
              sku,
              channel: regionVariables.channel,
            },
            context: CustomContext({
              client: APOLLO_CLIENTS.orderApp,
              disableErrorNotification: true,
            }),
          }),
        ]);

        setProductData({
          variantDetails,
          businessData,
          loading: false,
        });

        if (variantDetails?.productVariant?.product) {
          const productDetails = variantDetails.productVariant.product;
          const variations = getMultiselectValue(
            productDetails.bundleVariations,
          );

          if (variations && variations.length > 0) {
            setBundleVariations(variations);
          }
        }
      } catch (error) {
        console.error('Error fetching product data:', error);
        setProductData({
          loading: false,
          error: error as Error,
        });
      }
    };

    fetchProductData();
  }, [sku, locale]);

  useEffect(() => {
    if (bundleVariations.length === 0) return;

    const { country, locale: language } = getRegionFromLocale(locale);

    filterEnabledStraps(country, language.code, bundleVariations, false).then(
      (skus) => {
        setEnabledStrapSkus(skus);
      },
    );
  }, [bundleVariations]);

  useEffect(() => {
    if (enabledStrapSkus.length === 0) return;

    const fetchStrapData = async () => {
      const { languageCode } = getGqlRegionFromLocale(locale);
      try {
        const result = await fetchProductsBySkus({
          skus: enabledStrapSkus,
          languageCode: languageCode,
        });

        if (result) {
          setStrapData(result);
        }
      } catch (error) {
        console.error('Error fetching strap data:', error);
      }
    };

    fetchStrapData();
  }, [enabledStrapSkus, locale]);

  useEffect(() => {
    if (!productData.variantDetails?.productVariant?.product) return;

    const productDetails = productData.variantDetails.productVariant.product;

    let strapPreviewCoords = { x1: 415, x2: 800, y1: 840, y2: 900 };
    const coordsString = productDetails.media?.[0]?.strapPreviewCoordinates;
    if (coordsString && coordsString !== '') {
      try {
        strapPreviewCoords = JSON.parse(coordsString);
      } catch (e) {
        console.error('Failed to parse strap preview coordinates', e);
      }
    }

    const options: BundleVariationOption[] = [
      {
        coords: strapPreviewCoords,
        image: (productDetails.media?.[0]?.url ?? '').replace(
          '_512.',
          '_4096.',
        ),
        strapColor:
          productDetails.strapColor?.values?.[0]?.translation?.plainText ||
          getDropdownValue(productDetails.strapColor),
        strapColorCode:
          productDetails.strapColor?.values?.[0]?.translation?.name ||
          productDetails.strapColor?.values?.[0]?.name ||
          null,
        strapMaterial:
          productDetails.strapMaterial?.values?.[0]?.translation?.plainText ||
          getDropdownValue(productDetails.strapMaterial),
        slug: productDetails.slug,
        alt: productDetails.name,
      },
    ];

    let actualDifferentStrapsFound = false;

    if (strapData?.products?.edges) {
      strapData.products.edges.forEach((edge) => {
        if (edge?.node) {
          const isDifferentStrap =
            edge.node.strapColor?.values?.[0]?.translation?.plainText !==
              options[0].strapColor ||
            edge.node.strapMaterial?.values?.[0]?.translation?.plainText !==
              options[0].strapMaterial;

          if (isDifferentStrap) {
            actualDifferentStrapsFound = true;
          }

          options.push({
            coords: strapPreviewCoords,
            image: edge.node.thumbnail?.url?.replace('_512.', '_4096.'),
            strapColor:
              edge.node.strapColor?.values?.[0]?.translation?.plainText || null,
            strapColorCode:
              edge.node.strapColor?.values?.[0]?.translation?.name || null,
            strapMaterial:
              edge.node.strapMaterial?.values?.[0]?.translation?.plainText ||
              null,
            slug: edge.node.slug,
            alt: edge.node.thumbnail?.alt,
          });
        }
      });
    }

    const uniqueOptions = options.filter((option, index) => {
      if (index === 0) return true;

      const isDifferentStrap =
        option.strapColor !== options[0].strapColor ||
        option.strapMaterial !== options[0].strapMaterial;

      return isDifferentStrap;
    });

    setBundleVariationOptions(uniqueOptions);
    setHasDifferentStraps(actualDifferentStrapsFound);
  }, [productData.variantDetails, strapData]);

  // Only use URL parsing if retailerId wasn't provided as a prop
  useEffect(() => {
    if (retailerId) {
      return;
    }

    if (propRetailerId) {
      setRetailerId(propRetailerId);
      return;
    }

    // If no retailerId from props, try to extract from URL
    // This is a fallback for direct navigation to the page
    if (typeof window !== 'undefined') {
      const pathSegments = window.location.pathname.split('/');

      // Try to find retailerId - in URLs like /integrator/register/US102288/
      for (let i = 0; i < pathSegments.length; i++) {
        if (pathSegments[i] === 'register' && i + 1 < pathSegments.length) {
          const id = pathSegments[i + 1];
          setRetailerId(id);
          break;
        }
      }
    }
  }, [propRetailerId]);

  // Use the retailerId to fetch appointment status
  useEffect(() => {
    if (!retailerId) {
      return;
    }

    const fetchAppointmentStatusDirectly = async () => {
      setIsLoadingRetailer(true);

      try {
        const posManagerUrl = process.env.NEXT_PUBLIC_POS_MANAGER_APP_API_URI;

        if (!posManagerUrl) {
          console.error(
            'POS Manager URL is not defined in environment variables',
          );
          return;
        }

        const query = `
          query GetLocationAppointmentStatus($externalReference: String!) {
            locations(filter: { externalReference: $externalReference }) {
              edges {
                node {
                  id
                  name
                  externalReference
                  offersAppointment
                }
              }
            }
          }
        `;

        const variables = {
          externalReference: retailerId,
        };

        const response = await fetch(posManagerUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query,
            variables,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.data?.locations?.edges?.length > 0) {
          const hasAppointments =
            data.data.locations.edges[0].node.offersAppointment;
          setOffersAppointment(!!hasAppointments);
        } else {
          console.log('No location data found in direct fetch response');
        }
      } catch (error) {
        console.error('Error in direct fetch to POS Manager:', error);
      } finally {
        setIsLoadingRetailer(false);
      }
    };

    fetchAppointmentStatusDirectly();
  }, []);

  const handleCloseStrapModal = () => {
    setIsChangeStrapModalOpen(false);
    window.parentIFrame?.autoResize(true);
  };

  const loadProductBySku = (newSku: string) => {
    setProductData({ loading: true });
    setBundleVariationOptions([]);
    setEnabledStrapSkus([]);
    setBundleVariations([]);
    setStrapData(null);
    setHasDifferentStraps(false);

    const newProduct = { ...product, objectID: newSku };
    setTimeout(() => {
      (window as any).__tempRetailerProduct = newProduct;
      setProductData((prevState) => ({
        ...prevState,
        _forceUpdate: Date.now(),
      }));
    }, 0);
  };

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      (window as any).__tempRetailerProduct
    ) {
      const injectedProduct = (window as any).__tempRetailerProduct;
      (window as any).__tempRetailerProduct = null;
      if (injectedProduct.objectID !== sku) {
        const fetchProductData = async () => {
          try {
            const regionVariables = getGqlRegionFromLocale(locale);
            const client = initializeApollo();

            const [variantDetails, { data: businessData }] = await Promise.all([
              fetchProductDetail({
                sku: injectedProduct.objectID.toUpperCase(),
                channel: regionVariables.channel,
                languageCode: regionVariables.languageCode,
              }),

              client.query<
                OrderAppProductVariantBusinessDetailsQuery,
                OrderAppProductVariantBusinessDetailsQueryVariables
              >({
                query: ProductVariantBusinessDetailsDocument,
                variables: {
                  sku: injectedProduct.objectID,
                  channel: regionVariables.channel,
                },
                context: CustomContext({
                  client: APOLLO_CLIENTS.orderApp,
                  disableErrorNotification: true,
                }),
                fetchPolicy: 'network-only',
              }),
            ]);

            setProductData({
              variantDetails,
              businessData,
              loading: false,
            });

            if (variantDetails?.productVariant?.product) {
              const productDetails = variantDetails.productVariant.product;
              const variations = getMultiselectValue(
                productDetails.bundleVariations,
              );

              if (variations && variations.length > 0) {
                setBundleVariations(variations);
              }
            }
          } catch (error) {
            console.error('Error fetching new product data:', error);
            setProductData({
              loading: false,
              error: error as Error,
            });
          }
        };

        fetchProductData();
      }
    }
  }, [productData._forceUpdate]);

  const handleStrapOptionClick = (option: BundleVariationOption) => {
    if (option.slug) {
      if (option === bundleVariationOptions[0]) {
        handleCloseStrapModal();
        return;
      }

      handleCloseStrapModal();
      scrollToTop();
      loadProductBySku(option.slug);
    }
  };

  const handleCtaClick = () => {
    // Removed modal opening, only scroll to the form
    if (formRef.current) {
      document.documentElement.scrollTo({
        top: formRef.current.offsetTop,
        behavior: 'smooth',
      });
      window.parentIFrame?.scrollTo(0, formRef.current.offsetTop);
    }
  };

  if (productData.loading) {
    return (
      <S.DetailContainer className='retailer-widget-detail'>
        <CMSSection>
          <div className='product-detail-root'>
            <Loader
              data-test-id='RetailerProductDetailLoader'
              height={300}
              size='lg'
              variant='primary'
            />
          </div>
        </CMSSection>
      </S.DetailContainer>
    );
  }

  if (productData.error || !productData.variantDetails?.productVariant) {
    return (
      <S.DetailContainer className='retailer-widget-detail'>
        <CMSSection>
          <div className='product-detail-root'>
            <Typography color='error' marginY={5} scale='h3' textAlign='center'>
              {formatMessage(commonMessages.unableToLoadProductDetails)}
            </Typography>
          </div>
        </CMSSection>
      </S.DetailContainer>
    );
  }

  const variantDetails = productData.variantDetails.productVariant;

  if (!variantDetails?.product) {
    return (
      <S.DetailContainer className='retailer-widget-detail'>
        <CMSSection>
          <div className='product-detail-root'>
            <Typography color='error' marginY={5} scale='h3' textAlign='center'>
              Product information not available.
            </Typography>
          </div>
        </CMSSection>
      </S.DetailContainer>
    );
  }

  const productType = getProductType(
    variantDetails.product.productType || 'watch',
  );
  const productName = variantDetails.product.name || '';
  const productNameTranslated = getProductTranslatedName(
    variantDetails.product,
  );

  const description =
    variantDetails.product.specificText?.values[0]?.translation?.plainText ||
    variantDetails.product.specificText?.values[0]?.name ||
    variantDetails.product.description ||
    variantDetails.product.translation?.description ||
    '';

  const price = variantDetails.pricing?.price?.gross.amount;
  const currency = variantDetails.pricing?.price?.gross.currency;

  const collection = variantDetails.product.collections?.[0]?.name || '';
  const collectionName = getCollectionTranslatedName(variantDetails.product);

  const media = variantDetails.product.media || [];
  const mediaLarge = variantDetails.product.mediaLarge || [];
  const productDetails = variantDetails.product;

  const technicalDataSections: TechnicalDataSectionType[] = [
    {
      title: formatMessage(pdpMessages.technicalDataMovement),
      attributes: [
        {
          name: formatMessage(pdpMessages.technicalDataMovementCaliber),
          value: getDropdownValue(productDetails.caliber),
        },
        {
          name: formatMessage(pdpMessages.technicalDataMovementMovement),
          value: getDropdownValue(productDetails.movement),
        },
        {
          name: formatMessage(pdpMessages.technicalDataMovementPowerReserve),
          value: getDropdownValue(productDetails.powerReserve),
        },
        {
          name: formatMessage(pdpMessages.technicalDataMovementChronograph),
          value: getDropdownValue(productDetails.chronograph),
        },
        {
          name: formatMessage(pdpMessages.technicalDataMovementVibration),
          value: getDropdownValue(productDetails.vibration),
        },
        {
          name: formatMessage(pdpMessages.technicalDataMovementJewel),
          value: getDropdownValue(productDetails.jewel),
        },
        {
          name: formatMessage(pdpMessages.technicalDataMovementCalendar),
          value: getDropdownValue(productDetails.calendar),
        },
      ].filter((attr) => attr.value),
    },
    {
      title: formatMessage(pdpMessages.technicalDataCase),
      attributes: [
        {
          name: formatMessage(pdpMessages.technicalDataCaseMaterial),
          value: getDropdownValue(productDetails.caseMaterial),
        },
        {
          name: formatMessage(pdpMessages.technicalDataCaseCaseback),
          value: getDropdownValue(productDetails.caseback),
        },
        {
          name: formatMessage(pdpMessages.technicalDataCaseWaterResistance),
          value: getDropdownValue(productDetails.waterResistance),
        },
        {
          name: formatMessage(pdpMessages.technicalDataCaseBezel),
          value: getDropdownValue(productDetails.bezel),
        },
        {
          name: formatMessage(pdpMessages.technicalDataCaseCrown),
          value: getDropdownValue(productDetails.crown),
        },
        {
          name: formatMessage(pdpMessages.technicalDataCaseCrystal),
          value: getDropdownValue(productDetails.crystal),
        },
      ].filter((attr) => attr.value),
    },
    {
      title: formatMessage(pdpMessages.technicalDataDimensions),
      attributes: [
        {
          name: formatMessage(pdpMessages.technicalDataDimensionsProductWeight),
          value: getNumericValue(productDetails.weight)?.value
            ? `${getNumericValue(productDetails.weight)?.value} ${
                getNumericValue(productDetails.weight)?.unit || 'g'
              }`
            : undefined,
        },
        {
          name: formatMessage(
            pdpMessages.technicalDataDimensionsWatchHeadWeight,
          ),
          value: getNumericValue(productDetails.headWeight)?.value
            ? `${getNumericValue(productDetails.headWeight)?.value} ${
                getNumericValue(productDetails.headWeight)?.unit || 'g'
              }`
            : undefined,
        },
        {
          name: formatMessage(pdpMessages.technicalDataDimensionsDiameter),
          value: getNumericValue(productDetails.diameter)?.value
            ? `${getNumericValue(productDetails.diameter)?.value} mm`
            : undefined,
        },
        {
          name: formatMessage(pdpMessages.technicalDataDimensionsThickness),
          value: getNumericValue(productDetails.thickness)?.value
            ? `${getNumericValue(productDetails.thickness)?.value} mm`
            : undefined,
        },
        {
          name: formatMessage(pdpMessages.technicalDataDimensionsHeight),
          value: getNumericValue(productDetails.height)?.value
            ? `${getNumericValue(productDetails.height)?.value} mm`
            : undefined,
        },
        {
          name: formatMessage(pdpMessages.technicalDataDimensionsLugWidth),
          value: getNumericValue(productDetails.lugWidth)?.value
            ? `${getNumericValue(productDetails.lugWidth)?.value} mm`
            : undefined,
        },
      ].filter((attr) => attr.value),
    },
    {
      title: formatMessage(pdpMessages.technicalDataStrap),
      attributes: [
        {
          name: formatMessage(pdpMessages.technicalDataStrapStrapMaterial),
          value: getDropdownValue(productDetails.strapMaterial),
        },
        {
          name: formatMessage(pdpMessages.technicalDataStrapStrapColor),
          value: getDropdownValue(productDetails.strapColor),
        },
        {
          name: formatMessage(pdpMessages.technicalDataStrapStrapType),
          value: getDropdownValue(productDetails.strapType),
        },
        {
          name: formatMessage(pdpMessages.technicalDataStrapStrapLug),
          value: getDropdownValue(productDetails.lug),
        },
        {
          name: formatMessage(
            pdpMessages.technicalDataStrapStrapBuckleMaterial,
          ),
          value: getDropdownValue(productDetails.buckleMaterial),
        },
        {
          name: formatMessage(pdpMessages.technicalDataStrapStrapBuckleType),
          value: getDropdownValue(productDetails.buckleType),
        },
        {
          name: formatMessage(pdpMessages.technicalDataStrapStrapBuckleSize),
          value: getDropdownValue(productDetails.buckleSize),
        },
      ].filter((attr) => attr.value),
    },
  ].filter((section) => section.attributes.length > 0);

  const shareableProductData = {
    sku: sku,
    name: productName,
    collection: collection,
    description: description,
    image: media?.[0]?.url || '',
    url:
      typeof window !== 'undefined'
        ? `${window.location.origin}/retailer/${sku}`
        : `/retailer/${sku}`,
  };

  const productNameWithCollection = !productNameTranslated?.includes(
    collectionName.trim(),
  )
    ? (collectionName + ' ' + productNameTranslated).trim()
    : productNameTranslated;

  const caseMaterial = getDropdownValue(
    productData.variantDetails.productVariant.product.caseMaterial,
  );
  const dialColor = getDropdownValue(
    productData.variantDetails.productVariant.product.dialColor,
  );

  const handleChangeStrapModalOpen = (isOpen: boolean) => {
    scrollToTop();
    window.parentIFrame?.autoResize(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.parentIFrame?.resize(1024 as any);
    setIsChangeStrapModalOpen(isOpen);
  };

  return (
    <S.DetailContainer className='retailer-widget-detail'>
      <CMSSection>
        <div className='product-detail-root'>
          <ProductDetail>
            <ProductDetail.ImagesGallery
              alt={`${productName} - ${sku}`}
              data-test-id='ProductDetailImagesGallery'
              gallery={
                productType === 'strap'
                  ? 'strapPdpCarousel'
                  : 'watchPdpCarousel'
              }
              media={media}
              mediaLarge={mediaLarge}
              price={price}
              productType={productType}
              shareableProductData={shareableProductData}
              sku={sku}
              theCurrency={currency}
              withIcons={false}
            />

            <ProductDetail.DetailsCard>
              <ProductDetail.Sku data-test-id='ProductDetailSku'>
                {sku}
              </ProductDetail.Sku>
              <ProductDetail.Name data-test-id='ProductDetailName'>
                {highlightCollection(collectionName, productNameWithCollection)}
              </ProductDetail.Name>

              <Typography
                fontFamily='italianPlate'
                fontWeight='regular'
                my={2}
                scale='h6'
              >
                {caseMaterial} - {dialColor}
              </Typography>

              {description ? (
                <ProductDetail.Description
                  data-test-id='ProductDetailDescription'
                  description={description}
                  withBorderBottom={!!isDesktopView}
                />
              ) : (
                <Typography
                  color='secondaryGrey'
                  marginBottom={isDesktopView ? 4 : 2}
                  marginTop={2}
                  scale='p2'
                >
                  {formatMessage(pdpMessages.noDescriptionAvailable)}
                </Typography>
              )}

              {price && currency && (
                <ProductDetail.Pricing data-test-id='ProductDetailPricing'>
                  <Price price={{ amount: price, currency }} size='xl' />
                </ProductDetail.Pricing>
              )}

              {productType === 'watch' &&
                hasDifferentStraps &&
                bundleVariationOptions.length > 1 && (
                  <S.ChangeStrapButtonWrapper>
                    <ChangeStrapButton
                      coords={bundleVariationOptions[0].coords}
                      name={`${bundleVariationOptions[0].strapColor || ''} ${
                        bundleVariationOptions[0].strapMaterial || ''
                      }`}
                      setIsChangeStrapModalOpen={handleChangeStrapModalOpen}
                      url={bundleVariationOptions[0].image || ''}
                    />
                  </S.ChangeStrapButtonWrapper>
                )}

              <ProductDetail.CTA
                cta={
                  offersAppointment
                    ? ('BOOK_APPOINTMENT' as CtaType)
                    : ('CONTACT_US' as CtaType)
                }
                data-test-id={
                  offersAppointment
                    ? 'ProductDetailBookAppointmentCta'
                    : 'ProductDetailContactUsCta'
                }
                loading={isLoadingRetailer}
                onClick={handleCtaClick}
              />

              <Typography color='secondaryGrey' marginTop={3} scale='p3'>
                {formatMessage(pdpMessages.pricingDisclaimer)}
              </Typography>
            </ProductDetail.DetailsCard>
          </ProductDetail>
        </div>
      </CMSSection>
      {technicalDataSections.length > 0 && variantDetails.product && (
        <TechnicalData
          expandOnLoad={true}
          heading={formatMessage(pdpMessages.technicalDataTitle)}
          learnMoreLinkTarget='_blank'
          product={variantDetails.product}
          productType={productType as 'watch' | 'eyewear'}
          type={'technical data' as PlaceholderType}
          withSectionMargin={true}
        />
      )}
      <CMSSection style={{ paddingBottom: '0px', marginBottom: '0px' }}>
        <div ref={formRef}>
          {offersAppointment !== null &&
            (offersAppointment ? (
              <AppointmentForm
                multiDoors={multiDoors}
                retailerId={retailerId}
                sku={sku}
              />
            ) : (
              <RetailerContactUs
                productName={productName}
                retailerId={retailerId}
                sku={sku}
              />
            ))}
        </div>
      </CMSSection>

      <ChangeStrapModal
        bundleVariationOptions={bundleVariationOptions}
        close={() => handleCloseStrapModal()}
        isOpen={isChangeStrapModalOpen}
        onClose={() => handleCloseStrapModal()}
        onOptionClick={handleStrapOptionClick}
        scrollToTopWhenOpen={true}
      />
    </S.DetailContainer>
  );
};
